<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  FOCUS_QUEUE_FILTERS,
  useFocusConversationQueue,
} from 'dashboard/composables/useFocusConversationQueue';

const { t } = useI18n();
const {
  activeFilterKey,
  position,
  total,
  progressValue,
  setQueueFilter,
  exitFocus,
} = useFocusConversationQueue();

const isFilterOpen = ref(false);
const filterWrapRef = ref(null);

const filterLabel = key => {
  if (key === 'unread') return t('CHAT_LIST.QUICK_FILTERS.UNREAD');
  if (key === 'unattended') return t('CHAT_LIST.QUICK_FILTERS.UNANSWERED');
  return t('CHAT_LIST.QUICK_FILTERS.MINE');
};

const closeFilter = () => {
  isFilterOpen.value = false;
};

const toggleFilter = () => {
  isFilterOpen.value = !isFilterOpen.value;
};

const chooseFilter = key => {
  setQueueFilter(key);
  closeFilter();
};

const onDocClick = event => {
  if (!isFilterOpen.value) return;
  if (filterWrapRef.value?.contains(event.target)) return;
  closeFilter();
};

const onDocKeydown = event => {
  if (event.key !== 'Escape' || !isFilterOpen.value) return;
  closeFilter();
};

onMounted(() => {
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onDocKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick);
  document.removeEventListener('keydown', onDocKeydown);
});

const filterStepForKey = key => {
  if (key === 'ArrowDown' || key === 'ArrowRight') return 1;
  if (key === 'ArrowUp' || key === 'ArrowLeft') return -1;
  return 0;
};

const onFilterKeydown = event => {
  const step = filterStepForKey(event.key);
  if (!step) return;
  event.preventDefault();
  const current = FOCUS_QUEUE_FILTERS.indexOf(activeFilterKey.value);
  const next =
    FOCUS_QUEUE_FILTERS[
      (Math.max(0, current) + step + FOCUS_QUEUE_FILTERS.length) %
        FOCUS_QUEUE_FILTERS.length
    ];
  setQueueFilter(next);
  nextTick(() => {
    filterWrapRef.value?.querySelector(`[data-filter="${next}"]`)?.focus();
  });
};
</script>

<template>
  <header
    class="flex shrink-0 items-center gap-3 border-b border-n-weak px-4 py-2.5"
    data-testid="focus-queue-bar"
  >
    <div
      class="flex size-9 shrink-0 items-center justify-center rounded-full bg-n-iris-9 text-white"
      aria-hidden="true"
    >
      <span class="i-ri-focus-3-line size-[18px]" />
    </div>
    <div class="min-w-0 leading-tight">
      <b class="block font-extrabold text-[14px] text-n-slate-12">
        {{ t('SIDEBAR.FOCUS_MODE.TITLE') }}
      </b>
      <span class="text-[12.5px] text-n-slate-11">
        {{ t('CHAT_LIST.WORKBENCH.MODE_HIDDEN') }}
      </span>
    </div>

    <div class="mx-auto flex min-w-0 max-w-[420px] flex-1 items-center gap-2.5">
      <div
        role="progressbar"
        class="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-n-solid-2"
        :aria-valuenow="progressValue"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="t('CHAT_LIST.FOCUS_QUEUE.PROGRESS_LABEL')"
      >
        <div
          class="h-full rounded-full bg-n-iris-9 transition-[width] duration-[length:var(--motion-duration-layout)] ease-[var(--motion-ease-spring)]"
          :style="{ width: `${progressValue}%` }"
        />
      </div>

      <div ref="filterWrapRef" class="relative shrink-0">
        <button
          type="button"
          class="inline-flex min-h-11 items-center gap-1 whitespace-nowrap rounded-full px-2 text-[13px] font-bold tabular-nums text-n-slate-11 hover:bg-n-alpha-2"
          data-testid="focus-queue-filter"
          :aria-expanded="isFilterOpen"
          aria-haspopup="listbox"
          :aria-label="t('CHAT_LIST.FOCUS_QUEUE.COUNT_LABEL')"
          @click="toggleFilter"
        >
          <span class="text-n-slate-11">
            <b class="font-bold text-n-slate-12">{{ position || 0 }}</b>
            {{ t('CHAT_LIST.FOCUS_QUEUE.OF_TOTAL', { total: total || 0 }) }}
          </span>
          <span class="i-ri-arrow-down-s-line size-4" aria-hidden="true" />
        </button>

        <div
          v-if="isFilterOpen"
          class="absolute right-0 top-full z-20 mt-1 min-w-44 overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 p-1.5 shadow-xl"
          role="listbox"
          :aria-label="t('CHAT_LIST.FOCUS_QUEUE.COUNT_LABEL')"
          @keydown="onFilterKeydown"
        >
          <button
            v-for="key in FOCUS_QUEUE_FILTERS"
            :key="key"
            type="button"
            role="option"
            :aria-selected="activeFilterKey === key"
            :data-filter="key"
            :data-testid="`focus-queue-filter-${key}`"
            class="flex min-h-11 w-full items-center rounded-xl px-2.5 text-left text-sm hover:bg-n-alpha-2"
            :class="
              activeFilterKey === key
                ? 'bg-n-iris-9/15 font-semibold text-n-iris-11'
                : 'text-n-slate-12'
            "
            @click="chooseFilter(key)"
          >
            {{ filterLabel(key) }}
          </button>
        </div>
      </div>
    </div>

    <button
      type="button"
      class="inline-flex h-9 shrink-0 items-center rounded-full border border-n-weak px-4 text-[14px] font-bold text-n-slate-12 hover:bg-n-alpha-2"
      data-testid="focus-queue-exit"
      @click="exitFocus"
    >
      {{ t('CHAT_LIST.FOCUS_QUEUE.EXIT') }}
    </button>
  </header>
</template>
